/**
 * HostingPaymentCard — unified payment card for hosting checkout.
 * Shows Stripe and/or USDC buttons based on customer's payment methods.
 * Calls POST /chat/payments/confirm on button click.
 */

import React, { useState, useEffect } from 'react';
import api from '../utils/api';

export default function HostingPaymentCard({ metadata, conversationId, messageId }) {
  const {
    amount_display,
    description,
    site_deployment_id,
    has_card,
    has_wallet,
    restore,
    conversation_id: metaConversationId,
  } = metadata || {};

  const effectiveConversationId = conversationId || metaConversationId;

  const isPaid = metadata?.payment_completed === true;

  const [loading, setLoading] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [completed, setCompleted] = useState(isPaid);
  const [pendingUsdc, setPendingUsdc] = useState(false);
  // Link 20 F1: a pre-F1 card is never charged from — the server posts an up-to-date card and retires this one.
  const [stale, setStale] = useState(metadata?.card_state === 'superseded');

  // Sync external metadata changes from chat poll (scanner marks payment_completed)
  // completed is in the dep array to silence ESLint exhaustive-deps.
  // The guard (isPaid && !completed) ensures this only fires the false→true transition.
  useEffect(() => {
    if (isPaid && !completed) {
      setCompleted(true);
      setStatusMessage(null);
      setPendingUsdc(false);
    }
  }, [isPaid, completed]);

  // Sync card_state changes from chat poll (failure reset re-enables buttons)
  // pendingUsdc is in the dep array to silence ESLint exhaustive-deps.
  useEffect(() => {
    const cardState = metadata?.card_state;
    if (cardState === 'usdc_pending') {
      setPendingUsdc(true);
    } else if (pendingUsdc && !cardState) {
      // card_state was removed (failure endpoint cleared it) — re-enable buttons
      setPendingUsdc(false);
      setStatusMessage(null);
    }
  }, [metadata?.card_state, pendingUsdc]);

  const icon = restore ? '🔄' : '🏠';
  const title = description || 'Website Hosting';
  const amount = amount_display || '$44.99/year';

  const handlePay = async (rail) => {
    if (!effectiveConversationId || !site_deployment_id) return;
    setLoading(rail);
    setStatusMessage(null);
    try {
      const payload = {
        payment_type: 'hosting',
        payment_rail: rail,
        site_deployment_id,
        conversation_id: effectiveConversationId,
      };
      if (messageId) payload.message_id = messageId;
      const data = await api.post('/chat/payments/confirm', payload);
      if (data.action === 'redirect') {
        window.location.href = data.url;
	setCompleted(true);
      } else if (data.action === 'already_paid') {
        setCompleted(true);
        setStatusMessage(data.message || 'Hosting is already active.');
      } else if (data.action === 'usdc_payment') {
	//setCompleted(true);
        //setStatusMessage('USDC payment created — see below ↓');
        setPendingUsdc(true);
	setStatusMessage('Awaiting USDC payment — see below ↓');
      } else if (data.action === 'chat_update') {
	setCompleted(true);
        setStatusMessage(data.content);
      } else if (data.action === 'add_payment_method') {
        setStatusMessage('Add your card below ↓');
      } else if (data.action === 'show_picker') {
        setStatusMessage('Choose your payment method below ↓');
      } else if (data.action === 'stale_card') {
        setStale(true);
        setStatusMessage(data.message || 'This card is out of date — see the new one below ↓');
      } else if (data.action === 'error') {
        setStatusMessage(data.content);
      }
    } catch (err) {
      setStatusMessage(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="message-special">
      <div className="payment-picker">
        <p className="picker-prompt">
          {icon} <strong>{title}</strong> — <strong>{amount}</strong>
        </p>
        {completed ? (
          <p className="picker-status">✅ Paid</p>
        ) : stale ? (
          <p className="picker-status">{statusMessage || 'Replaced by a newer card below'}</p>
        ) : (
          <>
            <div className="picker-options">
              {has_card && (
                <button
                  className="picker-btn stripe-btn"
                  onClick={() => handlePay('stripe')}
                  disabled={loading !== null || completed || pendingUsdc}
                >
                  {loading === 'stripe' ? '⏳ Processing...' : '💳 Pay with Stripe (card)'}
                </button>
              )}
              {has_wallet && (
                <button
                  className="picker-btn usdc-btn"
                  onClick={() => handlePay('usdc')}
                  disabled={loading !== null || completed || pendingUsdc}
                >
                  {loading === 'usdc' ? '⏳ Processing...' : '◆ Pay with USDC (Base)'}
                </button>
              )}
            </div>
            {statusMessage && <p className="picker-status">{statusMessage}</p>}
          </>
        )}
      </div>
    </div>
  );
}
